import React, {useEffect, useRef} from "react";
import {useEditor, useEditorState, EditorContent, Editor} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {Placeholder} from "@tiptap/extensions";
import {Fragment, Slice} from "@tiptap/pm/model";
import {Button} from "reactstrap";
import {fromTelegramHtml, toTelegramHtml} from "./telegramHtml";
import {TelegramMacros} from "./telegramMacros";

type FormatName = "bold" | "italic" | "underline" | "strike";

const FORMATS: { name: FormatName; icon: string; title: string; toggle: (e: Editor) => void }[] = [
    {name: "bold", icon: "mdi-format-bold", title: "Bold (Ctrl+B)", toggle: e => e.chain().focus().toggleBold().run()},
    {
        name: "italic",
        icon: "mdi-format-italic",
        title: "Italic (Ctrl+I)",
        toggle: e => e.chain().focus().toggleItalic().run()
    },
    {
        name: "underline",
        icon: "mdi-format-underline",
        title: "Underline (Ctrl+U)",
        toggle: e => e.chain().focus().toggleUnderline().run()
    },
    {
        name: "strike",
        icon: "mdi-format-strikethrough-variant",
        title: "Strikethrough (Ctrl+Shift+S)",
        toggle: e => e.chain().focus().toggleStrike().run()
    },
];

interface TelegramEditorProps {
    value: string;
    onChange?: (value: string) => void;
    onBlur?: () => void;
    disabled?: boolean;
    readOnly?: boolean;
    invalid?: boolean;
    placeholder?: string;
    minHeight?: number;
}

const TelegramEditor: React.FC<TelegramEditorProps> = ({
                                                           value,
                                                           onChange,
                                                           onBlur,
                                                           disabled = false,
                                                           readOnly = false,
                                                           invalid = false,
                                                           placeholder = "Write a message...",
                                                           minHeight = 120,
                                                       }) => {
    const editable = !disabled && !readOnly;

    const onChangeRef = useRef(onChange);
    const onBlurRef = useRef(onBlur);
    onChangeRef.current = onChange;
    onBlurRef.current = onBlur;

    const lastValue = useRef(value);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: false,
                blockquote: false,
                bulletList: false,
                orderedList: false,
                listItem: false,
                codeBlock: false,
                code: false,
                horizontalRule: false,
                link: false,
            }),
            Placeholder.configure({placeholder}),
            TelegramMacros
        ],
        content: fromTelegramHtml(value),
        editable,
        editorProps: {
            clipboardTextParser: (text, _context, _plain, view) => {
                const {schema} = view.state;
                const lines = text.replace(/\r\n?/g, "\n").split("\n");
                const paragraphs = lines.map(line =>
                    schema.nodes.paragraph.create(null, line ? schema.text(line) : undefined)
                );
                return new Slice(Fragment.from(paragraphs), 1, 1);
            },
        },
        onUpdate: ({editor}) => {
            const html = toTelegramHtml(editor.getJSON());
            lastValue.current = html;
            onChangeRef.current?.(html);
        },
        onBlur: () => onBlurRef.current?.(),
    });

    useEffect(() => {
        editor?.setEditable(editable, false);
    }, [editor, editable]);

    useEffect(() => {
        if (!editor || value === lastValue.current) return;
        lastValue.current = value;
        editor.commands.setContent(fromTelegramHtml(value), {emitUpdate: false});
    }, [editor, value]);

    const active = useEditorState({
        editor,
        selector: ({editor}) => ({
            bold: editor?.isActive("bold"),
            italic: editor?.isActive("italic"),
            underline: editor?.isActive("underline"),
            strike: editor?.isActive("strike"),
        }),
    });

    const classes = [
        "tg-editor",
        invalid && "is-invalid",
        disabled && "is-disabled",
        readOnly && "is-readonly",
    ].filter(Boolean).join(" ");

    return (
        <div className={classes} style={{"--tg-editor-min-height": `${minHeight}px`} as React.CSSProperties}>
            {!readOnly && (
                <div className="tg-editor-toolbar">
                    {FORMATS.map(f => (
                        <Button
                            key={f.name}
                            type="button"
                            color="light"
                            size="sm"
                            title={f.title}
                            active={active?.[f.name]}
                            disabled={disabled || !editor}
                            onClick={() => editor && f.toggle(editor)}
                            className="px-2 py-1"
                        >
                            <i className={`mdi ${f.icon} font-size-16`}></i>
                        </Button>
                    ))}
                </div>
            )}

            <EditorContent editor={editor}/>
        </div>
    );
};

export default TelegramEditor;